import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import NumbersIcon from "@mui/icons-material/Numbers";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EuroIcon from "@mui/icons-material/Euro";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useLocation, useNavigate } from "react-router-dom";

// Dati utente
import { getUserDetails } from "../../api/patient";

// Pagamenti / movimenti (per recuperare id_pagamento)
import { fetchPayments, normalizeToLocaleDate } from "../../api/stats";

type TxData = {
  data: string;              // dd/mm/yyyy
  nome_completo: string;
  importo: number;           // EUR
  id_utente: number;
  // fallback
  nome?: string;
  cognome?: string;
};

type UserFetched = Record<string, any>;

const cardSx = {
  bgcolor: "background.paper",
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "none",
};

const fmtEUR = (v: number) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v);

/** Genera un indirizzo/città “leggibile” da vari campi possibili. */
function formatAddress(u?: UserFetched | null): string {
  if (!u) return "—";

  const parts: string[] = [];
  const possibleAddressKeys = ["indirizzo", "residenza", "via", "indirizzo_residenza"];
  const addr =
    possibleAddressKeys
      .map((k) => (u as any)?.[k])
      .find((v) => typeof v === "string" && v.trim().length > 0) || "";

  if (addr) parts.push(addr);

  const comune =
    u?.denominazione_comune ||
    u?.comune ||
    u?.citta ||
    "";
  if (comune) parts.push(comune);

  const prov =
    u?.denominazione_provincia ||
    u?.provincia ||
    u?.sigla_provincia ||
    "";
  if (prov) parts.push(prov);

  const regione = u?.denominazione_regione || u?.regione || "";
  if (regione) parts.push(regione);

  const final = parts.filter(Boolean).join(" - ");
  return final || "—";
}

const TransactionDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const row = (location.state || {}) as Partial<TxData>;

  const [user, setUser] = useState<UserFetched | null>(null);
  const [loadingUser, setLoadingUser] = useState(false);

  const [idTransazione, setIdTransazione] = useState<number | null>(null);
  const [loadingTx, setLoadingTx] = useState(false);

  // snackbar copy
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("Copiato!");

  const handleSnackClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setSnackOpen(false);
  };

  const copy = async (txt: string) => {
    try {
      await navigator.clipboard.writeText(txt);
      setSnackMsg("Copiato negli appunti");
      setSnackOpen(true);
    } catch {
      setSnackMsg("Impossibile copiare");
      setSnackOpen(true);
    }
  };

  // carica dettagli utente
  useEffect(() => {
    (async () => {
      if (!row?.id_utente) return;
      try {
        setLoadingUser(true);
        const u = await getUserDetails(row.id_utente);
        setUser(u as any);
      } catch (err) {
        console.error("Errore fetch user details:", err);
        setUser(null);
      } finally {
        setLoadingUser(false);
      }
    })();
  }, [row?.id_utente]);

  // carica pagamenti per ricavare id_pagamento
  useEffect(() => {
    (async () => {
      if (!row?.id_utente) return;
      try {
        setLoadingTx(true);
        const pays = await fetchPayments({ id_utente: row.id_utente });

        // cerco l'entry che combacia per data + importo
        const targetDate = (row.data || "").trim(); // dd/mm/yyyy
        const targetAmount = Number(row.importo || 0);

        const found = pays.find((p) => {
          const dateLocal = normalizeToLocaleDate(p.timestamp_creazione) || "";
          const cents =
            typeof p.prezzo === "string" ? parseInt(p.prezzo, 10) : (p.prezzo as number);
          const eur = (isFinite(cents) ? Number(cents) : 0) / 100;

          const sameDate = dateLocal === targetDate;
          const sameAmount = Math.abs(eur - targetAmount) < 0.01;

          return sameDate && sameAmount && p.id_pagamento != null;
        });

        setIdTransazione(found?.id_pagamento ?? null);
      } catch (err) {
        console.error("Errore fetch pagamenti:", err);
        setIdTransazione(null);
      } finally {
        setLoadingTx(false);
      }
    })();
  }, [row?.id_utente, row?.data, row?.importo]);

  // Nome completo con fallback
  const fullName = useMemo(() => {
    if (row?.nome_completo && row.nome_completo.trim()) return row.nome_completo;

    const n = (row?.nome || "").trim();
    const c = (row?.cognome || "").trim();
    if (n || c) return [n, c].filter(Boolean).join(" ");

    const un = (user?.nome || "").trim();
    const uc = (user?.cognome || "").trim();
    const joined = [un, uc].filter(Boolean).join(" ");
    return joined || "—";
  }, [row?.nome_completo, row?.nome, row?.cognome, user]);

  const email = (user?.email as string) || "—";
  const codiceFiscale = (user?.codice_fiscale as string) || "—";
  const codiceIstat = (user?.codice_istat as string) || "—";   // 👈 aggiunto
  const address = formatAddress(user);

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "calc(100vh - 64px)" }}>
      {/* Titolo + sottotitolo */}
      <Box sx={{ px: { xs: 3, md: 6 }, pt: 2, pb: 1 }}>
        <Typography variant="h5" sx={{ color: "#fff", fontWeight: 800 }}>
          Dettagli Transazione
        </Typography>
        <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.9)", mb: 1 }}>
          Informazioni del cliente e dettagli della transazione selezionata.
        </Typography>
      </Box>

      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* CARD 1: Informazioni Cliente */}
        <Card sx={{ ...cardSx, mb: 2, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Informazioni Cliente
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Dettagli del cliente per la fatturazione
          </Typography>

          <CardContent sx={{ pt: 0 }}>
            <Grid container spacing={2}>
              {/* Nome completo */}
              <Grid item xs={12} md={6}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 2 }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon color="action" />
                    <Typography fontWeight={600}>Nome Completo</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography>{fullName}</Typography>
                    <Tooltip title="Copia">
                      <IconButton size="small" onClick={() => copy(fullName)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>

              {/* Email */}
              <Grid item xs={12} md={6}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 2 }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <EmailIcon color="action" />
                    <Typography fontWeight={600}>Email</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography>{email}</Typography>
                    <Tooltip title="Copia">
                      <span>
                        <IconButton
                          size="small"
                          disabled={email === "—"}
                          onClick={() => copy(email)}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>

              {/* Codice Fiscale */}
              <Grid item xs={12} md={6}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 2 }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <NumbersIcon color="action" />
                    <Typography fontWeight={600}>Codice Fiscale</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography>{codiceFiscale}</Typography>
                    <Tooltip title="Copia">
                      <span>
                        <IconButton
                          size="small"
                          disabled={codiceFiscale === "—"}
                          onClick={() => copy(codiceFiscale)}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>

              {/* Codice ISTAT */}
              <Grid item xs={12} md={6}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 2 }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <NumbersIcon color="action" />
                    <Typography fontWeight={600}>Codice ISTAT</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography>{codiceIstat}</Typography>
                    <Tooltip title="Copia">
                      <span>
                        <IconButton
                          size="small"
                          disabled={codiceIstat === "—"}
                          onClick={() => copy(codiceIstat)}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>

              {/* Indirizzo */}
              <Grid item xs={12} md={6}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 2 }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationOnIcon color="action" />
                    <Typography fontWeight={600}>Indirizzo</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography>{address}</Typography>
                    <Tooltip title="Copia">
                      <span>
                        <IconButton
                          size="small"
                          disabled={address === "—"}
                          onClick={() => copy(address)}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* CARD 2: Dettagli Transazione */}
        <Card sx={{ ...cardSx, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Dettagli Transazione
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Informazioni sulla transazione e servizio acquistato
          </Typography>

          <CardContent sx={{ pt: 0 }}>
            <Grid container spacing={2}>
              {/* ID Transazione = id_pagamento */}
              <Grid item xs={12} md={6}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 2 }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <NumbersIcon color="action" />
                    <Typography fontWeight={600}>ID Transazione</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography>{loadingTx ? "..." : idTransazione ?? "—"}</Typography>
                    <Tooltip title="Copia">
                      <span>
                        <IconButton
                          size="small"
                          disabled={loadingTx || idTransazione == null}
                          onClick={() => copy(String(idTransazione))}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>

              {/* Importo */}
              <Grid item xs={12} md={6}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 2 }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <EuroIcon color="action" />
                    <Typography fontWeight={600}>Importo</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography fontWeight={700}>{fmtEUR(row.importo || 0)}</Typography>
                    <Tooltip title="Copia">
                      <IconButton
                        size="small"
                        onClick={() => copy(fmtEUR(row.importo || 0))}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>

              {/* Data */}
              <Grid item xs={12} md={6}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 2 }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarTodayIcon color="action" />
                    <Typography fontWeight={600}>Data</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography>{row.data || "—"}</Typography>
                    <Tooltip title="Copia">
                      <IconButton
                        size="small"
                        onClick={() => copy(row.data || "")}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>

              {/* Metodo di pagamento: default Stripe */}
              <Grid item xs={12} md={6}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 2 }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <CreditCardIcon color="action" />
                    <Typography fontWeight={600}>Metodo di Pagamento</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography>Stripe</Typography>
                    <Tooltip title="Copia">
                      <IconButton
                        size="small"
                        onClick={() => copy("Stripe")}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Azioni */}
            <Box mt={3} display="flex" gap={1} flexWrap="wrap">
              <Button
                variant="outlined"
                onClick={() =>
                  copy(
                    `${fullName}\nImporto: ${fmtEUR(
                      row.importo || 0
                    )}\nData: ${row.data || ""}\nTransazione: ${idTransazione ?? "—"}\nPagamento: Stripe`
                  )
                }
              >
                Copia Riepilogo
              </Button>
              <Button variant="outlined" onClick={() => navigate(-1)}>
                Torna Indietro
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Snackbar copia */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={1800}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackClose} severity="success" variant="filled" sx={{ width: "100%" }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TransactionDetails;
